<template>
  <Toolbar v-if="user" class="p-mb-4 p-mt-4">
    <template #start>
      <div style="font-size: 1.4em;">{{ $t('Companies') }}</div>
    </template>
  </Toolbar>
  <Spinner v-if="isLoading"/>
  <DesktopDataTable v-else
                    :dataTableIsLoading="dataTableIsLoading"
                    :itemsArrayIsEmpty="itemsArrayIsEmpty"
                    :sort="sort"
                    :showSearchInput="false"
                    :actionButtonsCount="actionButtonsCount"
                    @sort-items="sortItems"
                    :headers="[
                        {name: 'is_primary', width: '40px', sortable: false},
                        {name: '', title: $t('Company'), sortable: false},
                        // {name: 'is_primary', sortable: false},
                        {name: 'orders_count', class: 'ti-hummer', sortable: false},
                        {name: 'offers_count', class: 'ti-clipboard', sortable: false},
                        {name: 'cars_count', class: $store.getters.isForkliftTenant ? 'ti-truck' : 'ti-car', sortable: false},
                        {name: '', title: $t('Reg. number'), sortable: false},
                        {name: '', title: $t('VAT number'),sortable: false},
                        {name: '', title: $t('Email'), sortable: false},
                        {name: 'created_at', title: $t('Added'), sortable: true},
                    ]">
    <template v-slot:body>
      <tr v-for="(item, index) of items"
          :key="index"
          :class="{'updated-item': isItemUpdated(item.id)}">

        <td>
          <div class="p-d-flex p-ai-center">
            <i class="pi pi-star theme-color" v-if="item.is_primary" v-tooltip="$t('primaryRepresentativeLabel')"/>
<!--            <i class="pi pi-star pointer" v-else @click="setDefaultRepresentative(item)" v-tooltip="$t('setAsPrimaryRepresentativeTooltip')"/>-->
            <i class="pi pi-star"
               :class="{'pointer': canSetPrimaryRepresentative, 'p-disabled': !canSetPrimaryRepresentative }"
               v-else
               @click="setDefaultRepresentative(item)"
               v-tooltip="$t('setAsPrimaryRepresentativeTooltip')"/>
          </div>
        </td>

        <td>
          <CustomerData :user="item.companyUser"/>
        </td>

        <td>
          {{ item.companyUser?.orders_count ?? 0 }}
        </td>

        <td>
          {{ item.companyUser?.offers_count ?? 0 }}
        </td>
        <td>
          {{ item.companyUser?.cars_count ?? 0 }}
        </td>

        <td>
          <span v-if="item.companyUser?.reg_number">{{ item.companyUser.reg_number }}</span>
          <i v-else class="ti-minus"/>
        </td>

        <td>
          <span v-if="item.companyUser?.vat_number">{{ item.companyUser.vat_number }}</span>
          <i v-else class="ti-minus"/>
        </td>

        <td>
          <span v-if="item.companyUser?.email">{{ item.companyUser.email }}</span>
          <i v-else class="ti-minus"/>
        </td>



        <td class="table-date-line">
          {{ formatBy(item.created_at, item.creator) }}
        </td>
        <td>
          <div class="actions" v-if="item.companyUser?.uid">
            <router-link :to="{ path: `/customers/${item.companyUser.uid}` }" draggable="false" target="_blank" rel="noopener">
              <ViewButton :showSpinnerOnClick="false"/>
            </router-link>
          </div>
        </td>
      </tr>
    </template>
  </DesktopDataTable>

  <OwnPaginator v-show="!itemsArrayIsEmpty"
                :rows="pagination.perPage"
                :currentPage="pagination.currentPage - 1"
                :totalPages="pagination.pageCount"
                :totalRecords="pagination.totalCount"
                :rowsPerPageOptions="[5,10,25,50,100,200]"
                :pageLinkSize="11"/>

  <TotalRecordsData :pagination="pagination" :itemsArrayIsEmpty="itemsArrayIsEmpty"/>

</template>

<script>
import httpClient from '@/services/http.services'
import formatMixins from '@/mixins/formatMixins'
import datatableMixins from "@/mixins/datatableMixins";
import showErrorsMixins from "@/mixins/showErrorsMixins";
import settings from "@/settings";
import CustomerData from "@/components/CustomerData.vue";

export default {
  mixins: [ formatMixins, showErrorsMixins, datatableMixins ],
  components: {CustomerData },
  emits: ['update-items', 'change-customer-status'],
  props: {
    customerIsLoading: {
      type: Boolean,
      default: false
    },
    user: {
      type: Object,
      default: null
    },
  },
	data() {
		return {
      setPrimaryInFlight: false,
      fieldsExpand: 'companyUser.customerStatus,creator',
      apiLink: 'user/get-represented-companies',
      updatedItemId: null,
		}
	},
  watch: {
    '$route'() {
      const route = this.$route.path.split('/')
      if (this.$route.path === '/login' || route.indexOf('customers') === -1 || route.indexOf('represented-companies') === -1) {
        return false
      }
      this.checkRouteQuery()
      this.getItems()
    },
  },
  mounted() {
    if (!this.$route.params.uid) return
    this.checkRouteQuery()
    this.$store.commit('SET_FETCH_IN_FLIGHT', false)
    this.getPageData()
  },
  methods: {
    getPageData() {
      this.getItems()
    },
    async getItems() {
      if (!this.user?.id) return
      if (!this.userCanGetData || this.fetchInFlight) return
      this.$store.commit('SET_FETCH_IN_FLIGHT', true)
      this.dataTableIsLoading = true
      const ctrl = this.setController?.('customerRepresentedCompanies')
      try {
        const { status, data, headers } = await httpClient({
          url: this.apiLink,
          method: 'post',
          params: {
            id: this.user.id,
            page: this.pagination.currentPage,
            'per-page': this.pagination.perPage,
            search: this.processedSearchValue ?? null,
            expand: this.fieldsExpand,
          },
          signal: ctrl?.signal,
        })

        if (status === 200 && data && Array.isArray(data)) {
          const hasPaginationHeaders = !!headers['x-pagination-total-count']
          if (hasPaginationHeaders) {
            this.pagination.totalCount = +headers['x-pagination-total-count']
            this.pagination.pageCount  = +headers['x-pagination-page-count']
          } else {
            this.pagination.totalCount = data.length
            this.pagination.pageCount  = 1
          }

          this.items = data

          if (!data.length) {
            this.pagination.pageCount = 1
            this.pagination.currentPage = 1
          }
        } else {
          this.items = []
          this.showUnknownError()
        }
      } catch(err) {
        this.showError(err)
      } finally {
        this.$store.commit('SET_FETCH_IN_FLIGHT', false)
        this.isLoading = false
        this.disableEditButton = false
        this.dataTableIsLoading = false
        if (this._abortors?.customerRepresentedCompanies === ctrl) {
          this._abortors.customerRepresentedCompanies = null
        }
      }
    },
    async setDefaultRepresentative(item) {
      const representativeId = item?.id
      if (!representativeId || !this.canSetPrimaryRepresentative) return
      this.setPrimaryInFlight = true

      try {
        const { data, status } = await httpClient.post('user/set-primary-representative', { representativeId })
        if (status === 200 && data?.success) {
          this.items?.forEach(i => i.is_primary = false)
          item.is_primary = true
          this.$emit('update-items')
          this.$toast.add({ severity: 'success', detail: this.$t('Data updated'), life: settings.toastLife })
        } else {
          this.showUnknownError()
        }
      } catch (err) {
        this.showError(err)
      } finally {
        this.setPrimaryInFlight = false
      }
    },
	},
  computed: {
    actionButtonsCount() {
      return 1
    },
    canSetPrimaryRepresentative() {
      return this.$store.getters.isAccountantOrHigher && !this.setPrimaryInFlight
    },
  }
}
</script>